import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import "./App.css";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { FilterProvider } from "./context/FilterContext";
import { MoneyProvider } from "./context/MoneyContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Holdings from "./pages/Holdings";
import Analytics from "./pages/Analytics";
import Alerts from "./pages/Alerts";
import Instrument from "./pages/Instrument";
import InsurancePolicies from "./pages/InsurancePolicies";
import Investors from "./pages/Investors";
import InvestorDetail from "./pages/InvestorDetail";
import InvestorStudio from "./pages/InvestorStudio";
import Billing from "./pages/Billing";
import Admin from "./pages/Admin";
import KnowledgeBase from "./pages/KnowledgeBase";
import Messages from "./pages/Messages";
import LegalPage from "./pages/LegalPage";
import Security from "./pages/Security";
import Leaderboard from "./pages/Leaderboard";
import PostDetail from "./pages/PostDetail";
import Watchlist from "./pages/Watchlist";
import Transactions from "./pages/Transactions";
import Partner from "./pages/Partner";
import InvestorGuide from "./pages/InvestorGuide";
import ReferralLanding from "./pages/ReferralLanding";
import Identity from "./pages/Identity";
import Home from "./pages/Home";
import Register from "./pages/Register";
import ForInvestors from "./pages/ForInvestors";
import CookieConsent from "./components/CookieConsent";
import ErrorBoundary from "./components/ErrorBoundary";
import ImpersonationBanner from "./components/ImpersonationBanner";

function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="animate-spin text-brand-500" size={32} />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

/** Route guard for role-gated pages. The server enforces this too — this only stops
 *  someone landing on a page that would just render errors. */
function RoleGated({ children, allow }) {
  const { user, loading, role } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!allow.includes(role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

/** A route that only exists while its product area is open (backend/features.py).
 *  Closed means it does not exist, so it goes home rather than to an error. The API
 *  answers 404 for the same area whatever this does. */
function FeatureGated({ feature, children }) {
  const { features, featuresLoading } = useAuth();
  if (featuresLoading) return <FullPageLoader />;
  if (!features[feature]) return <Navigate to="/" replace />;
  return children;
}

/** A page that anyone may read, wrapped in the app shell for people who are signed in.
 *
 *  The leaderboard and public posts have to stay reachable logged-out — they're the
 *  shop window, and SEO depends on it. But rendering them bare for a signed-in
 *  member dropped them out of the app: no sidebar, no bottom nav, no way back
 *  except the browser button. Public and "outside the product" aren't the same
 *  thing, so the shell follows the session rather than the route.
 */
function PublicOrMember({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  return user ? <Layout>{children}</Layout> : children;
}

/** The domain root serves two different products depending on who's asking. */
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Home />;
  return <Layout><Dashboard /></Layout>;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={loading ? <FullPageLoader /> : user ? <Navigate to="/" replace /> : <Login />} />
      {/* Logged out => marketing homepage. Logged in => straight to the dashboard. */}
      <Route path="/" element={<RootRoute />} />
      <Route path="/for-investors" element={<FeatureGated feature="community"><ForInvestors /></FeatureGated>} />
      <Route path="/register" element={user ? <Navigate to="/" replace /> : <Register />} />
      <Route path="/holdings" element={<Protected><Holdings /></Protected>} />
      <Route path="/insurance" element={<Protected><InsurancePolicies /></Protected>} />
      <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
      <Route path="/alerts" element={<Protected><Alerts /></Protected>} />
      <Route path="/instrument/:ticker" element={<Protected><Instrument /></Protected>} />
      <Route path="/investors" element={<FeatureGated feature="community"><Protected><Investors /></Protected></FeatureGated>} />
      <Route path="/investors/:id" element={<FeatureGated feature="community"><Protected><InvestorDetail /></Protected></FeatureGated>} />
      <Route path="/billing" element={<FeatureGated feature="payments"><Protected><Billing /></Protected></FeatureGated>} />
      <Route path="/help" element={<Protected><KnowledgeBase /></Protected>} />
      <Route path="/messages" element={<FeatureGated feature="messages"><Protected><Messages /></Protected></FeatureGated>} />
      <Route path="/studio" element={<RoleGated allow={["investor", "admin"]}><InvestorStudio /></RoleGated>} />
      <Route path="/admin" element={<RoleGated allow={["admin"]}><Admin /></RoleGated>} />
      {/* Public by legal necessity: the Impressum and privacy policy must be
          reachable without an account (§5 DDG / DSGVO). */}
      <Route path="/imprint" element={<LegalPage pageKey="imprint" titleKey="legal.imprint" />} />
      <Route path="/privacy" element={<LegalPage pageKey="privacy" titleKey="legal.privacy" />} />
      <Route path="/terms" element={<LegalPage pageKey="terms" titleKey="legal.terms" />} />
      {/* /cookies was a separate policy that said the same things as the privacy one.
          Redirected rather than deleted: the old path is in consent records, in the
          sitemap Google already crawled, and quite possibly in a printed document. */}
      <Route path="/cookies" element={<Navigate to="/privacy" replace />} />
      {/* Public marketing/SEO surfaces — must render without a session while the
          community is open, and do not exist while it is closed. */}
      <Route path="/leaderboard" element={<FeatureGated feature="community"><PublicOrMember><Leaderboard /></PublicOrMember></FeatureGated>} />
      <Route path="/posts/:slug" element={<FeatureGated feature="community"><PublicOrMember><PostDetail /></PublicOrMember></FeatureGated>} />
      <Route path="/r/:handle" element={<FeatureGated feature="payments"><ReferralLanding /></FeatureGated>} />
      <Route path="/watchlist" element={<Protected><Watchlist /></Protected>} />
      <Route path="/transactions" element={<Protected><Transactions /></Protected>} />
      <Route path="/help/investor" element={<FeatureGated feature="community"><Protected><InvestorGuide /></Protected></FeatureGated>} />
      <Route path="/partner" element={<FeatureGated feature="payments"><RoleGated allow={["investor", "admin"]}><Partner /></RoleGated></FeatureGated>} />
      <Route path="/identity" element={<Protected><Identity /></Protected>} />
      <Route path="/security" element={<Protected><Security /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <MoneyProvider>
        <FilterProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ImpersonationBanner />
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
            <CookieConsent />
            <Toaster position="bottom-right" richColors closeButton />
          </BrowserRouter>
        </FilterProvider>
      </MoneyProvider>
    </AuthProvider>
  );
}
